import { aiContentTypeLabel } from '../model/presentation';
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AiContentListItem, HelpdeskAiContentKind } from '@shared/api';
import { useDebouncedUrlSearch } from '@shared/lib';
import {
  Button,
  Card,
  CardContent,
  CardHeaderRow,
  DescriptionList,
  Heading,
  PaginationControls,
  ResponsiveTable,
  SearchFilterToolbar,
  SearchInput,
  Text,
  type ResponsiveTableColumn,
} from '@shared/ui';
import { aiContentFormOptionsQuery, aiContentListQuery } from '../api/queries';
import { aiContentPageSize, type AiContentSearch } from '../model/search';
import { AiContentError } from './AiContentError';
import { AiContentActions } from './AiContentActions';
import { AiContentListDeletion } from './AiContentListDeletion';
export function AiContentList({
  kind,
  scope,
  search,
  onSearchChange,
  onOpen,
  onEdit,
  canDelete = false,
  onCreate,
}: {
  kind: HelpdeskAiContentKind;
  scope: string;
  search: AiContentSearch;
  onSearchChange: (next: AiContentSearch) => void;
  onOpen: (id: string) => void;
  onEdit?: (id: string) => void;
  canDelete?: boolean;
  onCreate?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const options = useQuery(aiContentFormOptionsQuery(scope, kind));
  const query = useQuery(
    aiContentListQuery(scope, kind, {
      q: search.aiQ?.trim() || undefined,
      page: search.aiPage ?? 1,
      pageSize: aiContentPageSize,
    })
  );
  const commit = useCallback(
    (aiQ: string | undefined) => {
      onSearchChange({ ...search, aiQ, aiPage: undefined });
    },
    [onSearchChange, search]
  );
  const draft = useDebouncedUrlSearch({ committedQuery: search.aiQ, onCommit: commit });
  const missing = t('crm.missingValue');
  const [deleteTarget, setDeleteTarget] = useState<AiContentListItem>();
  const [activeDeletionState, setActiveDeletionState] = useState<'pending' | 'failed'>();
  const closeDeletion = useCallback(() => {
    setDeleteTarget(undefined);
    setActiveDeletionState(undefined);
  }, []);
  const deletionState = (row: AiContentListItem) =>
    row.id === deleteTarget?.id
      ? (activeDeletionState ?? row.deletion?.state)
      : row.deletion?.state;
  const titleCell = (row: AiContentListItem) => (
    <div className="space-y-2">
      <Text size="inherit" className="break-words">
        {row.title ?? missing}
      </Text>
    </div>
  );
  const columns: ResponsiveTableColumn<AiContentListItem>[] = [
    {
      key: 'title',
      header: t('aiContent.fields.title'),
      size: 'flex',
      cell: titleCell,
    },
    {
      key: 'type',
      header: t('aiContent.fields.type'),
      size: 'medium',
      cell: (row) => aiContentTypeLabel(row.type, options.data, missing),
    },
    {
      key: 'updatedAt',
      header: t('aiContent.fields.updatedAt'),
      size: 'date',
      cell: (row) =>
        new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'medium' }).format(
          new Date(row.updatedAt)
        ),
    },
  ];
  const actions = (row: AiContentListItem) =>
    (onEdit && !deletionState(row)) || canDelete ? (
      <AiContentActions
        title={row.title}
        disabled={Boolean(deleteTarget)}
        onEdit={
          onEdit && !deletionState(row)
            ? () => {
                onEdit(row.id);
              }
            : undefined
        }
        onDelete={
          canDelete
            ? () => {
                setDeleteTarget(row);
              }
            : undefined
        }
      />
    ) : null;
  if (onEdit || canDelete)
    columns.push({
      key: 'actions',
      header: t('common.actions'),
      size: 'actionCompact',
      cell: actions,
    });
  const meta = query.data?.meta;
  const lifecyclePending = query.data?.items.some(
    (row) => row.creation?.state === 'pending' || row.deletion?.state === 'pending'
  );
  const isLoading =
    query.isPending || query.isPlaceholderData || (query.isFetching && !lifecyclePending);
  const outOfRange = !isLoading && meta && meta.totalItems > 0 && meta.page > meta.totalPages;
  const pagination =
    meta && meta.totalItems > 0 && meta.page <= meta.totalPages ? (
      <PaginationControls
        currentPage={search.aiPage ?? 1}
        pageSize={meta.pageSize}
        totalItems={meta.totalItems}
        disabled={isLoading}
        displayRange={
          isLoading
            ? {
                from: meta.totalItems === 0 ? 0 : (meta.page - 1) * meta.pageSize + 1,
                to: Math.min(meta.page * meta.pageSize, meta.totalItems),
                totalItems: meta.totalItems,
              }
            : undefined
        }
        onPageChange={(aiPage) => {
          onSearchChange({ ...search, aiPage });
        }}
        labels={{
          page: t('common.pagination.page'),
          showing: (from, to, total) => t('common.pagination.showing', { from, to, total }),
          of: (count) => t('common.pagination.of', { count }),
          previous: t('common.previous'),
          next: t('common.next'),
        }}
      />
    ) : null;
  return (
    <section className="min-w-0 space-y-5" aria-label={t(`aiContent.${kind}`)}>
      <Heading as="h1" className="sr-only">
        {t(`aiContent.${kind}`)}
      </Heading>
      <div className="flex flex-wrap items-end gap-3">
        <SearchFilterToolbar className="w-full sm:w-auto sm:flex-1">
          <SearchInput
            className="min-w-0 max-w-lg flex-1"
            maxLength={200}
            aria-label={t('aiContent.search')}
            placeholder={t('aiContent.search')}
            value={draft.draft}
            onChange={(e) => {
              draft.setDraft(e.target.value);
            }}
            onCompositionStart={draft.onCompositionStart}
            onCompositionEnd={(e) => {
              draft.onCompositionEnd(e.currentTarget.value);
            }}
          />
        </SearchFilterToolbar>
        {onCreate ? (
          <Button className="ml-auto" onClick={onCreate}>
            {t(`aiContent.create.${kind}`)}
          </Button>
        ) : null}
      </div>
      {deleteTarget ? (
        <AiContentListDeletion
          key={deleteTarget.id}
          scope={scope}
          kind={kind}
          id={deleteTarget.id}
          title={deleteTarget.title ?? missing}
          canDelete={canDelete}
          onClose={closeDeletion}
          onStateChange={setActiveDeletionState}
        />
      ) : null}
      {query.error ? (
        <AiContentError
          error={query.error}
          loading={query.isFetching}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : (
        <>
          {pagination}
          <ResponsiveTable
            rows={[...(query.data?.items ?? [])]}
            columns={columns}
            getRowKey={(row) => row.id}
            getRowNavigation={(row) => ({
              ariaLabel: row.title ?? missing,
              onNavigate: () => {
                onOpen(row.id);
              },
            })}
            loadingGeometryKey={`${scope}:${kind}`}
            breakpointClassName="lg"
            columnLayout="stable"
            isLoading={isLoading}
            loadingRowCount={aiContentPageSize}
            loadingLabel={t('common.loading')}
            ariaLabel={t(`aiContent.${kind}`)}
            emptyState={outOfRange ? false : { title: t(`aiContent.empty.${kind}`) }}
            renderMobileCard={(row) => (
              <Card interactive>
                <CardContent className="space-y-4 p-4">
                  <CardHeaderRow
                    controls={
                      onEdit || canDelete ? (
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {actions(row)}
                        </div>
                      ) : undefined
                    }
                  >
                    <Text className="min-w-0 break-words font-medium">{row.title ?? missing}</Text>
                  </CardHeaderRow>
                  <DescriptionList
                    columns={1}
                    mobileLayout="inline"
                    items={columns
                      .filter((c) => !['title', 'actions'].includes(c.key))
                      .map((c) => ({ id: c.key, term: c.header, description: c.cell(row) }))}
                  />
                </CardContent>
              </Card>
            )}
          />
          {pagination}
          {outOfRange ? (
            <Button
              variant="outline"
              onClick={() => {
                onSearchChange({ ...search, aiPage: meta.totalPages });
              }}
            >
              {t('helpdesk.lastPage')}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}
